import React, { useState, useEffect } from "react";
import { Form, Input, Button, message } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Auth } from "aws-amplify";
import { encrypt } from "Utility/Common/Methods";
import {
  emailPattern,
  FormItemRequiredRule,
  UserPasswordRequiredRule,
} from "Utility/Constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeftLong,
  faEye,
  faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";
import { useDispatch } from "react-redux";
import { login } from "Store/reducers/Auth/AuthSlice";
import { createAccountAction, getAccountDetails } from "Sdk/Actions";
import AuthRightContent from "@/components/AuthRightContent";

function SignupDetails() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [username] = useState(searchParams.get("username") || "");

  useEffect(() => {
    // Redirect if no username is provided
    if (!username) {
      navigate("/signup");
    }
  }, [username, navigate]);

  const handleCreateUser = async (userData) => {
    try {
      await createAccountAction(userData);
      const user = await Auth.currentAuthenticatedUser();
      await Auth.updateUserAttributes(user, {
        profile: "true",
      });
    } catch (err) {
      console.log("Error creating user:", err);
      throw err;
    }
  };

  const handleSubmit = async (data) => {
    setIsLoading(true);

    try {
      // 1. Sign up the user with autoSignIn enabled
      await Auth.signUp({
        username: data.email,
        password: data.password,
        attributes: {
          email: data.email,
          preferred_username: username,
          profile: "false",
        },
        autoSignIn: {
          enabled: true,
        },
      });

      // Store in localStorage
      localStorage.setItem(
        "cache",
        encrypt(
          JSON.stringify({ ...data, username }),
          process.env?.REACT_APP_SECUTRY_KEY
        )
      );

      // 2. Get the current authenticated user (should be auto-signed in)
      try {
        const currentUser = await Auth.currentAuthenticatedUser();

        const nextBillingAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

        // 3. Create user data in DynamoDB
        const userData = {
          username: username,
          email: data.email,
          name: String(username[0]).toUpperCase() + String(username).slice(1),
          nextBillingAt,
          userType: "trial",
          paidWidgets: ["review-generator", "seo"],
        };
        await handleCreateUser(userData);

        // 4. Store auth token in Redux
        dispatch(
          login({
            token: currentUser.signInUserSession.accessToken.jwtToken,
          })
        );

        // 5. Get account details
        await getAccountDetails(username);

        // 6. Navigate to dashboard
        navigate("/dashboard?new_signup=true");
      } catch (authError) {
        // If autoSignIn failed, we'll need to sign in manually
        const signInResponse = await Auth.signIn(data.email, data.password);

        // Repeat steps 3-6 here
        const nextBillingAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

        const userData = {
          username: username,
          email: data.email,
          name: String(username[0]).toUpperCase() + String(username).slice(1),
          nextBillingAt,
          userType: "trial",
          paidWidgets: ["review-generator", "seo"],
        };
        await handleCreateUser(userData);

        dispatch(
          login({
            token: signInResponse.signInUserSession.accessToken.jwtToken,
          })
        );

        await getAccountDetails(username);
        navigate("/dashboard?new_signup=true");
      }
    } catch (err) {
      console.log("Error in signup process:", err);
      let errorMessage =
        err?.code === "UsernameExistsException"
          ? "Looks like you already have an account, try logging in instead"
          : err?.message;
      message.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="max-w-5xl w-full">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left side - Content */}
          <div className="space-y-14">
            {/* Back button */}
            <button
              onClick={() => navigate("/signup")}
              className="flex items-center hover:text-gray-900 text-lg mb-4"
            >
              <FontAwesomeIcon icon={faArrowLeftLong} />
            </button>

            {/* Header */}
            <div className="mb-">
              <p className="text-lg mb-2">inprofile.co/{username} is yours!</p>
              <h1
                className="font-bold text-gray-900 leading-tight"
                style={{ fontSize: "32px" }}
              >
                Now, create your account.
              </h1>
            </div>

            {/* Form */}
            <div className="space-y-4">
              <Form
                onFinish={handleSubmit}
                layout="vertical"
                requiredMark={false}
                className="space-y-4"
              >
                <Form.Item
                  name="email"
                  rules={[
                    FormItemRequiredRule,
                    {
                      pattern: emailPattern,
                      message: "Please enter a valid email",
                    },
                  ]}
                >
                  <Input
                    placeholder="Email address"
                    className="py-3 px-4 input-border"
                    size="large"
                  />
                </Form.Item>
                <Form.Item
                  name="password"
                  rules={[FormItemRequiredRule, UserPasswordRequiredRule]}
                >
                  <Input.Password
                    placeholder="Password"
                    className="py-3 px-4 input-border"
                    size="large"
                    iconRender={(visible) => (
                      <FontAwesomeIcon 
                        icon={visible ? faEye : faEyeSlash}
                        className="cursor-pointer"
                      />
                    )}
                  />
                </Form.Item>

                <Button
                  htmlType="submit"
                  type="primary"
                  size="large"
                  loading={isLoading}
                  className="w-full h-12 text-base font-semibold rounded-lg bg-primary mt-6"
                >
                  Create Account
                </Button>
              </Form>

              {/* OR divider */}
              {/* <div className="flex items-center my-6">
                <div className="flex-1 border-t border-gray-300"></div>
                <span className="px-4 text-gray-500 text-sm font-medium">
                  OR
                </span>
                <div className="flex-1 border-t border-gray-300"></div>
              </div> */}

              {/* Google signup button */}
              {/* <Button
                size="large"
                className="w-full h-12 text-base font-semibold rounded-lg bg-blue-500 hover:bg-blue-600 text-white border-blue-500 hover:border-blue-600"
              >
                <span className="mr-2">G</span>
                Sign up with Google
              </Button> */}
            </div>
          </div>

          {/* Right side content */}
          <AuthRightContent />
        </div>
      </div>
    </div>
  );
}

export default SignupDetails;
